<template>
    <p-modal
        name="taxManagementModal"
        :label="modalTitle"
        ref="taxManagementModal"
        size="md"
        @ok.prevent="handleModalOk"
    >
        <template v-slot:default>
            <p-form ref="taxManagementForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>{{ modalDescription }}</p>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <p-card title="States" class="static-height">
                                <p-filter-select
                                    data-type="states"
                                    rules="required"
                                    :default-selected="false"
                                    :initial-display-count="10"
                                    :filter="stateFilter"
                                    v-model="selectedStates"
                                />
                            </p-card>
                        </b-col>
                    </b-row>
                    <b-row v-if="isUpdateMode">
                        <b-col>
                            <p-card title="Effective Date">
                                <p-datepicker
                                    label="Effective Date"
                                    v-model="effectiveDate"
                                    rules="required"
                                />
                            </p-card>
                        </b-col>
                    </b-row>
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import StoreListManager from '@/components/mixins/StoreListManager.js';

export default {
    mixins: [StoreListManager],
    props: {
        availableStates: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isBusy: false,
            isUpdateMode: false,
            selectedStates: [],
            effectiveDate: null,
            selectListOptions: {
                states: []
            }
        };
    },
    computed: {
        modalTitle() {
            return this.isUpdateMode ? 'Update State Taxes' : 'Load State Taxes';
        },
        modalDescription() {
            return this.isUpdateMode
                ? 'Select states to update existing tax rates with new values.'
                : 'Select states to load new tax rates into the system.';
        },
        stateFilter() {
            if (!this.availableStates || this.availableStates.length === 0) {
                return null;
            }
            return this.isUpdateMode
                ? s => this.availableStates.includes(s.abbreviation)
                : s => !this.availableStates.includes(s.abbreviation);
        }
    },
    methods: {
        showLoad() {
            this.isUpdateMode = false;
            this.selectedStates = [];
            this.effectiveDate = null;
            this.$refs.taxManagementModal.show();
        },
        showUpdate() {
            this.isUpdateMode = true;
            this.selectedStates = [];
            this.effectiveDate = new Date();
            this.$refs.taxManagementModal.show();
        },
        handleModalOk: async function() {
            if (!(await this.$refs.taxManagementForm.validate())) {
                return;
            }

            if (this.selectedStates.length === 0) {
                this.$appError(
                    'Please select one or more states.'
                );
                return;
            }

            this.isBusy = true;

            const endpoint = this.isUpdateMode ? 'taxes/updatetaxes' : 'taxes/loadtaxes';

            const stateAbbreviations = this.selectedStates.map(id =>
                this.selectListOptions.states.find(s => s.value === id)?.abbreviation
            ).filter(Boolean);

            const payload = {
                stateAbbreviations
            };

            if (this.isUpdateMode) {
                payload.effectiveDate = this.effectiveDate;
            }

            await axios
                .post(endpoint, payload)
                .then(result => {
                    const response = result.data;

                    this.$appSuccess(response.message)
                    this.$refs.taxManagementModal.hide();
                    this.$emit('data-saved', response.cronJobLogId);
                })
                .catch(error => {
                    const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                    this.$appError(
                            `Failed to ${this.isUpdateMode ? 'update' : 'load'} tax rates: ${errorMessage}`
                        )
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>

<style scoped>
.static-height {
    height: 370px;
}
</style>
